<template>
  <v-row>
    <v-col cols>
      <list-header :title="$t('collection.collections')">
        <template #filter>
          <filter-button :active-filters="getActiveFilters" @toggle="showFilter = !showFilter" />
        </template>
        <template #action>
          <sf-create-dialog
            :title="$t('collection.addCollection')"
            :loading="creating"
            @create="createCollection"
          />
        </template>
        <template #cols>
          <v-col cols="">
            <sort-button
              :sort="sortDirectionTitle.title"
              :active="activeSortButton === 'title'"
              :label="$t('title')"
              @click="toggleSortByTitle"
            />
          </v-col>
          <v-col cols="3" xl="2" class="d-flex justify-end">
            <sort-button
              :sort="SortDirection.DESC"
              :active="activeSortButton === 'lastModified'"
              :label="$t('collection.lastModified')"
              @click="toggleSortByLastModified"
            />
          </v-col>
          <v-col cols="3" xl="2" class="d-flex justify-end">
            <sort-button
              :sort="sortDirectionDate.date"
              :active="activeSortButton === 'date'"
              :label="$t('created')"
              @click="toggleSortByDate"
            />
          </v-col>
        </template>
      </list-header>

      <error-component v-if="mustHandleError" :error="error" transparent-background />
      <list-loading v-else-if="loading" />

      <list-empty
        v-else-if="collections.length === 0"
        :message="$t('noCollectionsFound')"
        :icon="mdiStarOutline"
      />

      <list-content v-else>
        <v-col cols="12">
          <collection-row
            v-for="(items, index) in collections"
            :key="items.id"
            :collection="items"
            :is-focused="currentFocusIndex === index"
            @keydown="onKeyDown"
            @focus="onItemFocus(index)"
            @blur="onItemBlur(index)"
          />
        </v-col>
        <v-col v-if="pages > 1" cols="12">
          <v-pagination
            :length="pages"
            :model-value="page"
            rounded
            total-visible="5"
            variant="flat"
            density="comfortable"
            active-color="primary"
            @update:model-value="goToPage"
          />
        </v-col>
      </list-content>
    </v-col>

    <mobile-side-menu v-if="$vuetify.display.mobile" :show="showFilter" @close="showFilter = false">
      <collections-filter
        ref="collections-filter-mobile"
        @close="showFilter = false"
        @update="searchCollections"
      />
    </mobile-side-menu>

    <v-col v-if="!$vuetify.display.mobile" v-show="showFilter" cols="3">
      <collections-filter
        ref="collections-filter"
        @close="showFilter = false"
        @update="searchCollections"
      />
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import MobileSideMenu from '@/components/MobileSideMenu.vue'
import SortButton from '@/components/SortButton.vue'
import SfCreateDialog from '@/components/dialog/SfCreateDialog.vue'
import FilterButton from '@/components/filter/FilterButton.vue'
import ListContent from '@/components/list/ListContent.vue'
import ListEmpty from '@/components/list/ListEmpty.vue'
import ListHeader from '@/components/list/ListHeader.vue'
import ListLoading from '@/components/list/ListLoading.vue'
import { useTitle } from '@/helpers'
import CollectionRow from '@/modules/collection/components/CollectionRow.vue'
import CollectionsFilter from '@/modules/collection/components/CollectionsFilter.vue'
import useCollections from '@/modules/collection/useCollections'
import useCollectionsFilter from '@/modules/collection/useCollectionsFilter'
import ErrorComponent from '@/modules/error/components/ErrorComponent.vue'
import { customErrorHandling } from '@/modules/error/helpers'
import CollectionEvent from '@/modules/tracking/events/collection'
import { trackEvent } from '@/modules/tracking/useTracking'
import router from '@/router'
import { SortDirection } from '@/types'
import { StorefrontCollectionSort } from '@/types/index'
import { useArrowKeyVerticalListNavigation } from '@/utils/useArrowKeyVerticalListNavigation'
import { mdiStarOutline } from '@mdi/js'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

defineOptions({ name: 'CollectionsPage' })

const { t } = useI18n()
useTitle(ref(t('collection.collections')))
const { error, mustHandleError } = customErrorHandling()

const currentRoute = useRoute()
const { collections, pages, loading, creating } = storeToRefs(useCollections())
const { selection, initialized, getActiveFilters } = storeToRefs(useCollectionsFilter())
const showFilter = ref(false)
const page = computed(() => {
  return parseInt(currentRoute.query.page as string) || 1
})

if (initialized.value) {
  useCollections().getCollections(page.value)
} else {
  useCollections().initCollections(page.value)
}

const sortDirectionDate = computed(() => {
  return {
    date:
      selection.value.sort === StorefrontCollectionSort.DATE_DESC
        ? SortDirection.DESC
        : SortDirection.ASC
  }
})
const sortDirectionTitle = computed(() => {
  return {
    title:
      selection.value.sort === StorefrontCollectionSort.TITLE_DESC
        ? SortDirection.DESC
        : SortDirection.ASC
  }
})
const activeSortButton = computed((): 'title' | 'date' | 'lastModified' | undefined => {
  const sort = useCollectionsFilter().selection.sort
  if (sort === StorefrontCollectionSort.LAST_MODIFIED) {
    return 'lastModified'
  } else if (
    sort === StorefrontCollectionSort.DATE_ASC ||
    sort === StorefrontCollectionSort.DATE_DESC
  ) {
    return 'date'
  } else if (
    sort === StorefrontCollectionSort.TITLE_ASC ||
    sort === StorefrontCollectionSort.TITLE_DESC
  ) {
    return 'title'
  } else {
    return undefined
  }
})

watch(currentRoute, () => {
  useCollections().getCollections(page.value)
})

const searchCollections = () => {
  goToPage(1)
  useCollections().getCollections(page.value)
}

const createCollection = (name: string) => {
  useCollections().createCollection(name)
  trackEvent(CollectionEvent.Create)
}

const goToPage = (newPage: number) => {
  if (page.value !== newPage) {
    router.push({
      name: 'Collections',
      query: { ...currentRoute.query, page: newPage.toString() }
    })
  }
}

const toggleSortByDate = () => {
  if (selection.value.sort === StorefrontCollectionSort.DATE_DESC) {
    useCollectionsFilter().selection.sort = StorefrontCollectionSort.DATE_ASC
    trackEvent(CollectionEvent.Sort.Date.Ascending)
  } else {
    useCollectionsFilter().selection.sort = StorefrontCollectionSort.DATE_DESC
    trackEvent(CollectionEvent.Sort.Date.Descending)
  }
  searchCollections()
}

const toggleSortByLastModified = () => {
  if (selection.value.sort === StorefrontCollectionSort.LAST_MODIFIED) {
    return
  }
  useCollectionsFilter().selection.sort = StorefrontCollectionSort.LAST_MODIFIED
  trackEvent(CollectionEvent.Sort.LastModified)
  searchCollections()
}

const toggleSortByTitle = () => {
  if (selection.value.sort === StorefrontCollectionSort.TITLE_DESC) {
    useCollectionsFilter().selection.sort = StorefrontCollectionSort.TITLE_ASC
    trackEvent(CollectionEvent.Sort.Title.Ascending)
  } else {
    useCollectionsFilter().selection.sort = StorefrontCollectionSort.TITLE_DESC
    trackEvent(CollectionEvent.Sort.Title.Descending)
  }
  searchCollections()
}

const numberOfCollections = computed(() => collections.value.length)

const { currentFocusIndex, onItemFocus, onItemBlur, onKeyDown } =
  useArrowKeyVerticalListNavigation(numberOfCollections)
</script>
